import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import {
    Loader2, Calendar, Plane, Files,
    Users, Eye, FileStack, CheckCircle2, XCircle, AlertCircle,
    MapPin, DollarSign,
    Building2,
    Backpack, Pencil
} from 'lucide-react';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Link } from '@tanstack/react-router';
import { Badge } from '@/components/ui/badge';

import { toast } from 'react-toastify';
import { useAuth } from '@/context/AuthContext';
import { AdminTravelsService, type TravelDocument } from '@/lib/collections/requestsTravels/rrhh/getRequestTravelRrhh';
import { approveTravel, rejectTravel } from '@/lib/collections/requestsTravels/rrhh/approved-rejectedRrhh';
import { countries, ecuadorProvinces } from '../utils/countrys-provinces';
import { useUsersMap } from '@/lib/collections/requestsTravels/rrhh/useCollaboratorsMap';
import { sendTravelStatusToN8n } from '../webhook/sendTravelStatusToN8n';
import { fetchCompanies, type Company } from '@/lib/collections/organizations/getOrganizationsFetch';
import { getSubordinateUserIds } from '@/lib/collections/supervisor/getSubordinateIds';
import { fetchUsersWithProfiles, type CombinedUser } from '@/lib/manager realtime/userCache';


// helper
const COUNTRY_MAP = Object.fromEntries(
    countries.map(country => [country.value, country.label])
);

const PROVINCE_MAP = Object.fromEntries(
    ecuadorProvinces.map(province => [province.value, province.label])
);

// Funciones helper
const getCountryLabel = (value: string) => COUNTRY_MAP[value] || value;
const getProvinceLabel = (value: string) => PROVINCE_MAP[value] || value;


let initialTravelId: string | null = null;

// Capturar INMEDIATAMENTE cuando se carga el módulo
if (typeof window !== 'undefined') {
    const params = new URLSearchParams(window.location.search);
    initialTravelId = params.get('travelId');
}

const ViewRrhhTravels = () => {
    const { user, userRoles } = useAuth();


    const [_isDirectLink, setIsDirectLink] = useState(false);


    // Estados principales
    const [travels, setTravels] = useState<TravelDocument[]>([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [usersError, setUsersError] = useState<string | null>(null);

    // Estados de filtros
    const now = new Date();
    const currentYear = now.getFullYear().toString();
    const currentMonth = String(now.getMonth() + 1).padStart(2, '0');
    const [selectedYear, setSelectedYear] = useState<string>(currentYear);
    const [selectedMonth, setSelectedMonth] = useState<string>(currentMonth);
    const [selectedUser, setSelectedUser] = useState<string>('all');
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const { getUserName, getUserEmail } = useUsersMap();

    const [companiesMap, setCompaniesMap] = useState<Map<string, string>>(new Map());

    //traer los registros de los subordinados del supervisor
    const [subordinateIds, setSubordinateIds] = useState<string[]>([]);

    const [page, setPage] = useState(1);
    const limit = 12;


    // Estados de usuarios
    const [users, setUsers] = useState<CombinedUser[]>([]); // Para el dropdown
    const [loadingUsers, setLoadingUsers] = useState(true);

    // Estados de estadísticas
    const [globalStats, setGlobalStats] = useState({
        totalRecords: 0,
        totalUsers: 0,
        pending: 0,
        approved: 0,
        rejected: 0
    });

    const [userStats, setUserStats] = useState<{
        totalRecords: number;
        pending: number;
        approved: number;
        rejected: number;
    } | null>(null);

    // Estados para detalle y acciones
    const [selectedTravel, setSelectedTravel] = useState<TravelDocument | null>(null);
    const [showDetail, setShowDetail] = useState(false);
    const [showApproveDialog, setShowApproveDialog] = useState(false);
    const [showRejectDialog, setShowRejectDialog] = useState(false);
    const [rejectComment, setRejectComment] = useState('');
    const [actionLoading, setActionLoading] = useState(false);

    // Cargar usuarios y estadísticas globales al montar
    useEffect(() => {
        loadUsers();
    }, []);


    //effect para obtener los  registros de subordinados
    useEffect(() => {
        if (userRoles.isSupervisor && user?.$id) {
            getSubordinateUserIds(user.$id).then(setSubordinateIds);
        }
    }, [userRoles.isSupervisor, user?.$id]);

    useEffect(() => {
        loadGlobalStats();
    }, [selectedYear, selectedMonth]);

    // Cargar viajes cuando cambien los filtros
    useEffect(() => {
        loadTravels();
    }, [selectedYear, selectedMonth, selectedUser, page, searchQuery, statusFilter, subordinateIds]);

    // Cargar estadísticas del usuario cuando cambie
    useEffect(() => {
        if (selectedUser !== 'all') {
            loadUserStats(selectedUser);
        } else {
            setUserStats(null);
        }
    }, [selectedUser, selectedYear, selectedMonth]);


    useEffect(() => {
        if (initialTravelId) {
            setSearchQuery(initialTravelId);
            setIsDirectLink(true);

            // Limpiar para que no se vuelva a aplicar
            initialTravelId = null;
        }
    }, []);



    // Helper
    const getCompanyName = (id: string) => companiesMap.get(id) || id;

    useEffect(() => {
        fetchCompanies().then(data => {
            const map = new Map<string, string>();
            data.forEach((company: Company) => map.set(company.$id, company.name || company.commercialName));
            setCompaniesMap(map);
        });
    }, []);

    const loadUsers = async () => {
        try {
            setLoadingUsers(true);
            setUsersError(null);

            const [usersData, userIdsWithTravels] = await Promise.all([
                fetchUsersWithProfiles(),
                AdminTravelsService.getUsersWithTravels(),
            ]);

            const usersWithTravels = usersData.filter(
                (user) => userIdsWithTravels.includes(user.id) && user.profile,
            );
            setUsers(usersWithTravels);
        } catch (error) {
            console.error("Error loading users:", error);
            setUsersError("Error al cargar usuarios");
        } finally {
            setLoadingUsers(false);
        }
    };

    const loadGlobalStats = async () => {
        try {
            let monthFilter: string | undefined = undefined;
            if (selectedYear !== 'all' && selectedMonth !== 'all') {
                monthFilter = `${selectedYear}-${selectedMonth}`;
            }

            const stats = await AdminTravelsService.getGlobalStats(monthFilter);
            setGlobalStats(stats);
        } catch (error) {
            console.error('Error loading global stats:', error);
        }
    };

    const loadUserStats = async (userId: string) => {
        try {
            let monthFilter: string | undefined = undefined;
            if (selectedYear !== 'all' && selectedMonth !== 'all') {
                monthFilter = `${selectedYear}-${selectedMonth}`;
            }

            const stats = await AdminTravelsService.getUserStats(userId, monthFilter);
            setUserStats(stats);
        } catch (error) {
            console.error('Error loading user stats:', error);
            setUserStats(null);
        }
    };

    const loadTravels = async () => {
        try {
            if (userRoles.isSupervisor && !userRoles.isRRHH && !userRoles.isAdmin && subordinateIds.length === 0) {
                return;
            }

            if (!searchQuery.trim()) {
                setLoading(true);
            }
            setError(null);

            let monthFilter: string | undefined = undefined;

            if (selectedYear !== 'all' && selectedMonth !== 'all') {
                monthFilter = `${selectedYear}-${selectedMonth}`;
            }

            const offset = (page - 1) * limit;
            const result = await AdminTravelsService.getAllTravels(
                {
                    userId: selectedUser,
                    month: monthFilter,
                    searchQuery: searchQuery,
                    statusFilter: statusFilter,
                    allowedUserIds: userRoles.isSupervisor && !userRoles.isRRHH && !userRoles.isAdmin
                        ? subordinateIds
                        : undefined,
                },
                limit,
                offset
            );

            setTravels(result.documents);
            setTotal(result.total);
        } catch {
            setError('No se pudo cargar la información. Intenta de nuevo.');
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetail = (travel: TravelDocument) => {
        setSelectedTravel(travel);
        setShowDetail(true);
    };

    const handleApproveClick = (travel: TravelDocument) => {
        setSelectedTravel(travel);
        setShowApproveDialog(true);
    };

    const handleRejectClick = (travel: TravelDocument) => {
        setSelectedTravel(travel);
        setRejectComment('');
        setShowRejectDialog(true);
    };

    const handleConfirmApprove = async () => {
        if (!selectedTravel || !user?.$id) return;
        try {
            setActionLoading(true);
            await approveTravel(selectedTravel.$id, user.$id);

            // ← webhook
            await sendTravelStatusToN8n(
                'approved',
                selectedTravel.$id,
                selectedTravel.title,
                getUserName(selectedTravel.userId),
                getUserEmail(selectedTravel.userId),
                undefined,        // comentario de rrhh dejarlo asi
                user.email
            );

            toast.success('Viaje aprobado exitosamente');
            setShowApproveDialog(false);
            loadTravels();
            loadGlobalStats();
            if (selectedUser !== 'all') loadUserStats(selectedUser);
        } catch (error) {
            toast.error('Error al aprobar el viaje, intente de nuevo');
        } finally {
            setActionLoading(false);
        }
    };

    const handleConfirmReject = async () => {
        if (!selectedTravel || !user?.$id) return;
        if (!rejectComment.trim()) { toast.warning('Debe ingresar un motivo de rechazo'); return; }
        try {
            setActionLoading(true);
            await rejectTravel(selectedTravel.$id, user.$id, rejectComment.trim());


            await sendTravelStatusToN8n(
                'rejected',
                selectedTravel.$id,
                selectedTravel.title,
                getUserName(selectedTravel.userId),
                getUserEmail(selectedTravel.userId),  //email del que creo la solicitud
                rejectComment.trim(),
                user.email
            );

            toast.success('Viaje rechazado');
            setShowRejectDialog(false);
            setRejectComment('');
            loadTravels();
            loadGlobalStats();
            if (selectedUser !== 'all') loadUserStats(selectedUser);
        } catch (error) {
            toast.error('Error al rechazar el viaje');
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('es-EC', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatTime = (dateString: string) => {
        return new Date(dateString).toLocaleTimeString('es-EC', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status: string) => {
        const variants = {
            pending: { color: 'bg-yellow-100 text-yellow-800 border-yellow-200', label: 'Pendiente', icon: AlertCircle },
            approved: { color: 'bg-green-100 text-green-800 border-green-200', label: 'Aprobado', icon: CheckCircle2 },
            rejected: { color: 'bg-red-100 text-red-800 border-red-200', label: 'Rechazado', icon: XCircle }
        };
        const variant = variants[status as keyof typeof variants] || variants.pending;
        const Icon = variant.icon;

        return (
            <Badge className={`${variant.color} border flex items-center gap-1`}>
                <Icon className="h-3 w-3" />
                {variant.label}
            </Badge>
        );
    };

    const getYearOptions = () => {
        const options = [];
        const currentYear = new Date().getFullYear();
        for (let i = 0; i < 5; i++) {
            const year = currentYear - i;
            options.push({ value: year.toString(), label: year.toString() });
        }
        return options;
    };

    const getMonthOptions = () => {
        return [
            { value: '01', label: 'Enero' },
            { value: '02', label: 'Febrero' },
            { value: '03', label: 'Marzo' },
            { value: '04', label: 'Abril' },
            { value: '05', label: 'Mayo' },
            { value: '06', label: 'Junio' },
            { value: '07', label: 'Julio' },
            { value: '08', label: 'Agosto' },
            { value: '09', label: 'Septiembre' },
            { value: '10', label: 'Octubre' },
            { value: '11', label: 'Noviembre' },
            { value: '12', label: 'Diciembre' }
        ];
    };

    const totalPages = Math.ceil(total / limit);
    const displayStats = userStats || globalStats;
    const isUserSelected = selectedUser !== 'all';

    if (loading && travels.length === 0) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    return (
        <>
            <Breadcrumb>
                <BreadcrumbList>
                    <BreadcrumbItem>
                        <div className="flex items-center gap-1.5">
                            <Files />
                            <BreadcrumbPage>Permisos</BreadcrumbPage>
                        </div>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                            <Link to="/requests">Menu</Link>
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbSeparator />
                    <BreadcrumbItem>
                        <BreadcrumbPage className="hidden sm:block">Administrativo - Viajes</BreadcrumbPage>
                        <BreadcrumbPage className="block sm:hidden"><Plane className="w-5 h-5" /></BreadcrumbPage>
                    </BreadcrumbItem>
                </BreadcrumbList>
            </Breadcrumb>

            <div className="space-y-6 pt-6">
                <div className="bg-primary/5 border border-primary/20 rounded-lg p-4">
                    <div className="flex items-center gap-2">
                        <Calendar className="h-5 w-5 text-primary" />
                        <div>
                            <p className="text-sm font-semibold text-primary">
                                Estadísticas de {getMonthOptions().find(m => m.value === selectedMonth)?.label} {selectedYear}
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {selectedUser === 'all'
                                    ? 'Todos los usuarios'
                                    : getUserName(selectedUser)
                                }
                            </p>
                        </div>
                    </div>
                </div>

                {/* Dashboard de Estadísticas */}
                <div className="grid gap-4 grid-cols-2 lg:grid-cols-5">
                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">
                                {isUserSelected ? 'Registros Usuario' : 'Total Registros'}
                            </CardTitle>
                            <div className="rounded-lg bg-primary/10 p-2">
                                <FileStack className="h-4 w-4 text-primary" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{displayStats.totalRecords}</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {isUserSelected ? `De ${getUserName(selectedUser)}` : 'En el sistema'}
                            </p>
                        </CardContent>
                    </Card>

                    {!isUserSelected && (
                        <Card className="shadow-sm hover:shadow-md transition-shadow">
                            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                <CardTitle className="text-sm font-medium">Usuarios</CardTitle>
                                <div className="rounded-lg bg-blue-500/10 p-2">
                                    <Users className="h-4 w-4 text-blue-500" />
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="text-2xl font-bold">{globalStats.totalUsers}</div>
                                <p className="text-xs text-muted-foreground mt-1">Con viajes registrados</p>
                            </CardContent>
                        </Card>
                    )}

                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Aprobados</CardTitle>
                            <div className="rounded-lg bg-green-500/10 p-2">
                                <CheckCircle2 className="h-4 w-4 text-green-500" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{displayStats.approved}</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {displayStats.totalRecords > 0
                                    ? `${Math.round((displayStats.approved / displayStats.totalRecords) * 100)}% del total`
                                    : '0% del total'}
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Pendientes</CardTitle>
                            <div className="rounded-lg bg-yellow-500/10 p-2">
                                <AlertCircle className="h-4 w-4 text-yellow-500" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{displayStats.pending}</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {displayStats.totalRecords > 0
                                    ? `${Math.round((displayStats.pending / displayStats.totalRecords) * 100)}% del total`
                                    : '0% del total'}
                            </p>
                        </CardContent>
                    </Card>

                    <Card className="shadow-sm hover:shadow-md transition-shadow">
                        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                            <CardTitle className="text-sm font-medium">Rechazados</CardTitle>
                            <div className="rounded-lg bg-red-500/10 p-2">
                                <XCircle className="h-4 w-4 text-red-500" />
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-bold">{displayStats.rejected}</div>
                            <p className="text-xs text-muted-foreground mt-1">
                                {displayStats.totalRecords > 0
                                    ? `${Math.round((displayStats.rejected / displayStats.totalRecords) * 100)}% del total`
                                    : '0% del total'}
                            </p>
                        </CardContent>
                    </Card>
                </div>

                {/* Header con filtros */}
                <div className="flex flex-col gap-6">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <h2 className="text-3xl font-bold bg-linear-to-r from-primary to-primary/60 bg-clip-text text-transparent">
                                Gestión de Viajes
                            </h2>
                            <p className="text-sm text-muted-foreground mt-1">
                                {total} {total === 1 ? 'viaje encontrado' : 'viajes encontrados'}
                            </p>
                        </div>
                    </div>

                    {/* Filtros */}
                    <div className="flex flex-wrap gap-3">
                        <div className="w-full sm:flex-1 sm:max-w-md">
                            <Input
                                type="text"
                                placeholder="Buscar por título, destino, objetivo..."
                                value={searchQuery}
                                onChange={(e) => {
                                    setSearchQuery(e.target.value);
                                    setPage(1);
                                }}
                                className="w-full"
                            />
                        </div>

                        <Select
                            value={statusFilter}
                            onValueChange={(value) => {
                                setStatusFilter(value);
                                setPage(1);
                            }}
                        >
                            <SelectTrigger className="w-45">
                                <SelectValue placeholder="Estado" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Todos los estados</SelectItem>
                                <SelectItem value="pending">Pendiente</SelectItem>
                                <SelectItem value="approved">Aprobado</SelectItem>
                                <SelectItem value="rejected">Rechazado</SelectItem>
                            </SelectContent>
                        </Select>

                        {usersError ? (
                            <div className="flex items-center gap-2">
                                <Alert variant="destructive" className="py-2 px-3 flex-1">
                                    <AlertDescription className="text-xs">{usersError}</AlertDescription>
                                </Alert>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={loadUsers}
                                    disabled={loadingUsers}
                                    className="shrink-0"
                                >
                                    {loadingUsers ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                    ) : (
                                        'Reintentar'
                                    )}
                                </Button>
                            </div>
                        ) : (
                            <Select
                                value={selectedUser}
                                onValueChange={(value) => {
                                    setSelectedUser(value);
                                    setPage(1);
                                }}
                                disabled={loadingUsers}
                            >
                                <SelectTrigger className="w-50">
                                    <SelectValue placeholder="Usuario" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Todos los usuarios</SelectItem>
                                    {users.map(user => (
                                        <SelectItem key={user.id} value={user.id}>
                                            {user.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        )}

                        <Select value={selectedYear} onValueChange={(value) => {
                            setSelectedYear(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-32.5">
                                <SelectValue placeholder="Año" />
                            </SelectTrigger>
                            <SelectContent>
                                {getYearOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>

                        <Select value={selectedMonth} onValueChange={(value) => {
                            setSelectedMonth(value);
                            setPage(1);
                        }}>
                            <SelectTrigger className="w-35">
                                <SelectValue placeholder="Mes" />
                            </SelectTrigger>
                            <SelectContent>
                                {getMonthOptions().map(option => (
                                    <SelectItem key={option.value} value={option.value}>
                                        {option.label}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                {/* Error */}
                {error && (
                    <Alert variant="destructive">
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Grid de viajes */}
                {travels.length === 0 ? (
                    <Card className="border-dashed">
                        <CardContent className="py-16 text-center">
                            <div className="flex flex-col items-center gap-4">
                                <div className="rounded-full bg-muted p-6">
                                    <Plane className="h-12 w-12 text-muted-foreground" />
                                </div>
                                <div>
                                    <p className="text-lg font-medium">No hay viajes registrados</p>
                                    <p className="text-sm text-muted-foreground mt-1">
                                        {searchQuery
                                            ? 'Intenta ajustar los filtros de búsqueda'
                                            : 'No se encontraron viajes con los filtros seleccionados'}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ) : (
                    <>
                        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                            {travels.map((travel) => {
                                const showActions = travel.statusByRrhh === 'pending' || travel.statusByRrhh === 'rejected';

                                return (
                                    <Card
                                        key={travel.$id}
                                        className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-500 dark:border-l-blue-500 overflow-hidden flex flex-col"
                                    >
                                        <CardHeader className="pb-2 px-4 pt-4">
                                            {/* Badge de estado arriba a la derecha */}
                                            <div className="flex justify-between items-start gap-2 mb-2">
                                                <div className="flex items-center gap-2 flex-1 min-w-0">
                                                    <div className="rounded-lg bg-primary/10 p-1.5 shrink-0">
                                                        <Backpack className="h-3.5 w-3.5 text-primary" />
                                                    </div>
                                                    <h3 className="font-semibold text-sm line-clamp-2 leading-tight">
                                                        {travel.title}
                                                    </h3>
                                                </div>
                                                <div className="shrink-0">
                                                    {getStatusBadge(travel.statusByRrhh)}
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                                <Users className="h-3.5 w-3.5 shrink-0" />
                                                <span className="font-medium truncate">{getUserName(travel.userId)}</span>
                                            </div>

                                            <p className="text-xs text-muted-foreground line-clamp-2 mt-1.5">
                                                {travel.tripPurpose || 'Sin objetivo especificado'}
                                            </p>
                                        </CardHeader>

                                        <CardContent className="pt-1 pb-4 px-4 space-y-2.5 flex-1 flex flex-col">
                                            <div className="space-y-2 flex-1">
                                                <div className="flex items-center gap-2">
                                                    <div className="rounded-md bg-blue-500/10 p-1.5 shrink-0">
                                                        <Calendar className="h-3 w-3 text-blue-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-[10px] text-muted-foreground leading-none mb-0.5">Fechas</p>
                                                        <p className="font-medium text-xs leading-tight">
                                                            {formatDate(travel.startDate)} – {formatDate(travel.endDate)}
                                                        </p>
                                                    </div>
                                                </div>

                                                {travel.isInternational ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="rounded-md bg-purple-500/10 p-1.5 shrink-0">
                                                            <MapPin className="h-3 w-3 text-purple-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[10px] text-muted-foreground leading-none mb-0.5">Internacional</p>
                                                            <p className="font-medium text-xs truncate leading-tight">
                                                                {travel.countries?.map(c => getCountryLabel(c)).join(', ') || 'No especificado'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="flex items-center gap-2">
                                                        <div className="rounded-md bg-orange-500/10 p-1.5 shrink-0">
                                                            <MapPin className="h-3 w-3 text-orange-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[10px] text-muted-foreground leading-none mb-0.5">Nacional</p>
                                                            <p className="font-medium text-xs truncate leading-tight">
                                                                {travel.provinces?.map(p => getProvinceLabel(p)).join(', ') || 'No especificado'}
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {travel.estimatedCost && travel.estimatedCost.length > 0 && (
                                                    <div className="flex items-center gap-2">
                                                        <div className="rounded-md bg-green-500/10 p-1.5 shrink-0">
                                                            <DollarSign className="h-3 w-3 text-green-600" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-[10px] text-muted-foreground leading-none mb-0.5">Costo estimado</p>
                                                            <p className="font-medium text-xs leading-tight">
                                                                ${travel.estimatedCost.reduce((t, c) => t + c, 0).toFixed(2)} USD
                                                            </p>
                                                        </div>
                                                    </div>
                                                )}

                                                {travel.commentByRrhh && (
                                                    <div className="p-2 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                                                        <p className="text-[10px] text-red-800 dark:text-red-200 font-medium mb-0.5">
                                                            Comentario RRHH:
                                                        </p>
                                                        <p className="text-[10px] text-red-700 dark:text-red-300 line-clamp-2">
                                                            {travel.commentByRrhh}
                                                        </p>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Botones primarios */}
                                            <div className="pt-2 border-t flex gap-1.5 mt-auto">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className="h-8 flex-1 text-xs cursor-pointer"
                                                    onClick={() => handleViewDetail(travel)}
                                                >
                                                    <Eye className="h-3.5 w-3.5" />
                                                    <span className="ml-1">Ver</span>
                                                </Button>
                                                {userRoles.isAdmin && (
                                                    <Link to="/requests/viewTravel/$id/editTravel" params={{ id: travel.$id }} className="flex-1">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 w-full text-xs cursor-pointer text-yellow-600 border-yellow-200 hover:bg-yellow-50 dark:text-yellow-400 dark:border-yellow-800 dark:hover:bg-yellow-900/20"
                                                        >
                                                            <Pencil className="h-3.5 w-3.5" />
                                                            <span className="ml-1"></span>
                                                        </Button>
                                                    </Link>
                                                )}
                                            </div>

                                            {/* Aprobar / Rechazar */}
                                            {showActions && (
                                                <div className="flex gap-1.5">
                                                    <Button
                                                        size="sm"
                                                        className="flex-1 h-8 text-xs bg-blue-500 hover:bg-blue-600 text-white cursor-pointer"
                                                        onClick={() => handleApproveClick(travel)}
                                                    >
                                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                                        <span className="ml-1">Aprobar</span>
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        className="flex-1 h-8 text-xs cursor-pointer"
                                                        onClick={() => handleRejectClick(travel)}
                                                    >
                                                        <XCircle className="h-3.5 w-3.5" />
                                                        <span className="ml-1">Rechazar</span>
                                                    </Button>
                                                </div>
                                            )}
                                        </CardContent>
                                    </Card>
                                );
                            })}
                        </div>

                        {/* Paginación */}
                        {totalPages > 1 && (
                            <div className="flex justify-center items-center gap-3 pt-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                    disabled={page === 1 || loading}
                                    className="cursor-pointer"
                                >
                                    Anterior
                                </Button>
                                <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-md">
                                    <span className="text-sm font-medium">Página {page}</span>
                                    <span className="text-muted-foreground">de</span>
                                    <span className="text-sm font-medium">{totalPages}</span>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages || loading}
                                    className="cursor-pointer"
                                >
                                    Siguiente
                                </Button>
                            </div>
                        )}
                    </>
                )}

                {/* Dialog de Aprobar */}
                <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Aprobar Solicitud de Viaje</DialogTitle>
                            <DialogDescription>
                                ¿Está seguro de aprobar el viaje « {selectedTravel?.title} »? No podrá cancelarlo después.
                            </DialogDescription>
                        </DialogHeader>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => setShowApproveDialog(false)}
                                disabled={actionLoading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={handleConfirmApprove}
                                disabled={actionLoading}
                                className="bg-green-600 hover:bg-green-700 cursor-pointer"
                            >
                                {actionLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Aprobando...
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="h-4 w-4 mr-2" />
                                        Aprobar
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Dialog de Rechazar */}
                <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Rechazar Solicitud de Viaje</DialogTitle>
                            <DialogDescription>
                                Ingrese el motivo por el cual se rechaza el viaje "{selectedTravel?.title}"
                            </DialogDescription>
                        </DialogHeader>
                        <div className="py-4">
                            <Textarea
                                placeholder="Motivo del rechazo..."
                                value={rejectComment}
                                onChange={(e) => setRejectComment(e.target.value)}
                                className="min-h-30"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setShowRejectDialog(false);
                                    setRejectComment('');
                                }}
                                disabled={actionLoading}
                            >
                                Cancelar
                            </Button>
                            <Button
                                variant="destructive"
                                onClick={handleConfirmReject}
                                disabled={actionLoading}
                                className="cursor-pointer"
                            >
                                {actionLoading ? (
                                    <>
                                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                        Rechazando...
                                    </>
                                ) : (
                                    <>
                                        <XCircle className="h-4 w-4 mr-2" />
                                        Rechazar
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Modal de detalle */}
                <Dialog open={showDetail} onOpenChange={setShowDetail}>
                    <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
                        <DialogHeader>
                            <DialogTitle className="flex items-center gap-2">
                                <Plane className="h-5 w-5" />
                                {selectedTravel?.title}
                            </DialogTitle>
                            <DialogDescription>
                                <div className="flex items-center gap-2">
                                    <Users className="h-4 w-4" />
                                    {selectedTravel && getUserName(selectedTravel.userId)}
                                </div>
                            </DialogDescription>
                        </DialogHeader>

                        {!selectedTravel ? (
                            <div className="py-12 text-center text-muted-foreground">Cargando...</div>
                        ) : (
                            <div className="space-y-4 font-outfit">
                                <div className="grid gap-4">
                                    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                        <label className="text-sm font-medium text-muted-foreground">Estado:</label>
                                        {getStatusBadge(selectedTravel.statusByRrhh)}
                                    </div>

                                    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                        <label className="text-sm font-medium text-muted-foreground">Usuario:</label>
                                        <p className="text-sm font-medium">{getUserName(selectedTravel.userId)}</p>
                                    </div>

                                    {selectedTravel.isGroup && selectedTravel.usersId.length > 1 && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Colaboradores:</label>
                                            <div className="text-sm">
                                                {selectedTravel.usersId
                                                    .filter(id => id !== selectedTravel.userId)
                                                    .map(id => getUserName(id))
                                                    .join(', ')}
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                        <label className="text-sm font-medium text-muted-foreground">Objetivo del viaje:</label>
                                        <p className="text-sm leading-relaxed wrap-break-word whitespace-pre-wrap">
                                            {selectedTravel.tripPurpose}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                        <label className="text-sm font-medium text-muted-foreground">Fechas:</label>
                                        <p className="text-sm font-medium">
                                            {formatDate(selectedTravel.startDate)} ({formatTime(selectedTravel.startDate)})
                                            {' - '}
                                            {formatDate(selectedTravel.endDate)} ({formatTime(selectedTravel.endDate)})
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                        <label className="text-sm font-medium text-muted-foreground">Tipo de viaje:</label>
                                        <p className="text-sm font-medium">
                                            {selectedTravel.isInternational ? 'Internacional' : 'Nacional'}
                                        </p>
                                    </div>

                                    <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                        <label className="text-sm font-medium text-muted-foreground">Destino:</label>
                                        <p className="text-sm">
                                            {selectedTravel.isInternational
                                                ? selectedTravel.countries?.map(country => getCountryLabel(country)).join(', ') || 'No especificado'
                                                : selectedTravel.provinces?.map(province => getProvinceLabel(province)).join(', ') || 'No especificado'}
                                        </p>
                                    </div>

                                    {selectedTravel.address && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Dirección:</label>
                                            <p className="text-sm">{selectedTravel.address}</p>
                                        </div>
                                    )}

                                    {selectedTravel.transportationType && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Transporte:</label>
                                            <p className="text-sm">
                                                {selectedTravel.transportationType === 'land' && 'Terrestre'}
                                                {selectedTravel.transportationType === 'air' && 'Aéreo'}
                                                {selectedTravel.transportationType === 'sea' && 'Marítimo'}
                                            </p>
                                        </div>
                                    )}

                                    {selectedTravel.estimatedCost && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Costo estimado:</label>
                                            <p className="text-sm font-medium">
                                                ${selectedTravel.estimatedCost.reduce((total, cost) => total + cost, 0).toFixed(2)} USD
                                            </p>
                                        </div>
                                    )}

                                    {selectedTravel.companies && selectedTravel.companies.length > 0 && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Empresas a visitar:</label>
                                            <ul className="text-sm space-y-1">
                                                {selectedTravel.companies.map((companyId, index) => (
                                                    <div key={index} className="flex items-center gap-1.5 bg-muted/60 border rounded-lg px-3 py-1.5 text-sm">
                                                        <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                                                        {getCompanyName(companyId)}
                                                    </div>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {selectedTravel.clients && selectedTravel.clients.length > 0 && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Clientes:</label>
                                            <ul className="text-sm space-y-1">
                                                {selectedTravel.clients.map((client, index) => (
                                                    <li key={index}>• {client}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {selectedTravel.idApprovedByRrhh && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Aprobado por:</label>
                                            <p className="text-sm font-medium">{getUserName(selectedTravel.idApprovedByRrhh)}</p>
                                        </div>
                                    )}


                                    {selectedTravel.idRejectedByRrhh && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Rechazado por:</label>
                                            <p className="text-sm font-medium">{getUserName(selectedTravel.idRejectedByRrhh)}</p>
                                        </div>
                                    )}

                                    {selectedTravel.commentByRrhh && (
                                        <div className="grid grid-cols-[140px_1fr] gap-2 items-start">
                                            <label className="text-sm font-medium text-muted-foreground">Comentario RRHH:</label>
                                            <div className="p-3 bg-red-50 dark:bg-red-900/20  border rounded-2xl border-red-200 dark:border-red-800">
                                                <p className="text-sm text-red-700 dark:text-red-300 whitespace-pre-wrap">
                                                    {selectedTravel.commentByRrhh}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-[140px_1fr] gap-2 items-start pt-2 border-t">
                                        <label className="text-sm font-medium text-muted-foreground">Fecha de registro:</label>
                                        <p className="text-sm text-muted-foreground">
                                            {formatDate(selectedTravel.$createdAt)} a las {formatTime(selectedTravel.$createdAt)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </>
    );
};

export default ViewRrhhTravels;